import { useState } from 'react';
import Drawer from '@liquidweb-dev/undercurrent/Drawer';
import useAssetDetails from 'modules/queries/asset/useDetails';
import hostingDetailsAlsowith from 'pages/servers/details/hostingDetailsAlsowith';
import DialogDestroyAcronis from 'components/DialogDestroyAcronis';
import DialogDestroyCloudBackup from 'components/DialogDestroyCloudBackup/DialogDestroyCloudBackup';
import BackupTypeSwitch from './BackupTypeSwitch';
import BackupSettingsError from './Error';
import BackupSettingsLoading from './Loading';

export interface BackupSettingsProps
	extends Omit<React.ComponentProps<typeof Drawer>, 'onClose'> {
	uniqId?: string;
	onClose: () => void;
}

const BackupSettings: React.FC<BackupSettingsProps> = ({
	onClose,
	uniqId,
	...rest
}) => {
	const [destroyAcronisUniqId, setDestroyAcronisUniqId] = useState('');
	const [destroyCloudBackupTarget, setDestroyCloudBackupTarget] = useState<{
		uniqId: string;
		domain?: string;
	}>({ uniqId: '' });

	const { open } = rest;

	const {
		data: assetDetails,
		isLoading: isAssetDetailsLoading,
		isError: isAssetDetailsError,
	} = useAssetDetails(
		{
			alsowith: hostingDetailsAlsowith,
			uniq_id: uniqId || '',
		},
		{
			enabled: Boolean(uniqId && open),
			refetchInterval: 30000,
		},
	);

	const handleDestroyAcronis = () => {
		const acronisUniqId = assetDetails?.backupsAndStorage?.acronis?.uniq_id;
		if (!acronisUniqId) return;

		setDestroyAcronisUniqId(acronisUniqId);
		onClose();
	};

	const handleDestroyAcronisDialogClose = () => {
		setDestroyAcronisUniqId('');
	};

	const handleDestroyCloudBackup = () => {
		if (!assetDetails?.uniq_id) return;

		setDestroyCloudBackupTarget({
			uniqId: assetDetails.uniq_id,
			domain: assetDetails.domain,
		});
		onClose();
	};

	const handleDestroyCloudBackupDialogClose = () => {
		setDestroyCloudBackupTarget({ uniqId: '' });
	};

	return (
		<>
			<Drawer anchor="right" onClose={onClose} {...rest}>
				{isAssetDetailsLoading && <BackupSettingsLoading onClose={onClose} />}
				{!isAssetDetailsLoading && assetDetails && (
					<BackupTypeSwitch
						assetDetails={assetDetails}
						onDestroyAcronis={handleDestroyAcronis}
						onDestroyCloudBackup={handleDestroyCloudBackup}
						onClose={onClose}
					/>
				)}
				{isAssetDetailsError && <BackupSettingsError onClose={onClose} />}
			</Drawer>
			<DialogDestroyAcronis
				open={destroyAcronisUniqId !== ''}
				uniqId={destroyAcronisUniqId}
				onClose={handleDestroyAcronisDialogClose}
			/>
			<DialogDestroyCloudBackup
				open={destroyCloudBackupTarget.uniqId !== ''}
				uniqId={destroyCloudBackupTarget.uniqId}
				domain={destroyCloudBackupTarget.domain}
				onClose={handleDestroyCloudBackupDialogClose}
			/>
		</>
	);
};

export default BackupSettings;
